import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getCertificate, getCurrentRates } from '@/lib/api';
import { formatInr, MONEY_CLASSNAME } from '@/lib/money';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Shield, CheckCircle, QrCode, TrendingUp, Gem, Droplets, Sparkles } from 'lucide-react';

const CARE_GUIDES = {
  gold: {
    title: 'Caring for Your Gold',
    tips: [
      { heading: 'How to Clean', text: 'Soak in warm water with a few drops of mild dish soap for 15-20 minutes. Gently brush with a soft-bristle toothbrush. Rinse with clean water and pat dry with a lint-free cloth.' },
      { heading: 'How to Store', text: 'Store each piece separately in a soft cloth pouch or lined jewelry box. Keep away from humidity. Never store gold with other metals to prevent scratching.' },
      { heading: 'What to Avoid', text: 'Remove before swimming (chlorine damages gold), applying perfume, exercising, or using cleaning chemicals. Avoid contact with bleach, hairspray, and hand sanitiser.' },
    ]
  },
  silver: {
    title: 'Caring for Your Silver',
    tips: [
      { heading: 'How to Clean', text: 'Use a silver polishing cloth for routine care. For tarnish, make a paste of baking soda and water, apply gently with a soft cloth, rinse, and dry thoroughly.' },
      { heading: 'How to Store', text: 'Store in anti-tarnish bags or cloth. Add silica gel packets to your jewelry box to absorb moisture. Keep away from direct sunlight.' },
      { heading: 'Preventing Tarnish', text: 'Wear your silver often — natural oils keep it shiny. Remove before showering, swimming, or sleeping. Apply lotion and perfume before putting on silver.' },
    ]
  }
};

const PURITY_INFO = {
  '24K': { standard: 'IS 1417:2016', fineness: 999, description: 'Pure gold, 99.9% gold content. Softest form, ideal for investment-grade bars and coins.' },
  '22K': { standard: 'IS 1417:2016', fineness: 916, description: '91.6% gold content. Traditional Indian jewelry standard. Durable yet rich in gold.' },
  '18K': { standard: 'IS 1417:2016', fineness: 750, description: '75% gold content. International standard for fine jewelry. Excellent durability.' },
  '999 Silver': { standard: 'IS 2112:2012', fineness: 999, description: 'Fine silver, 99.9% pure. Investment grade. Used in premium collectibles and bullion.' },
  '925 Silver': { standard: 'IS 2112:2012', fineness: 925, description: 'Sterling silver, 92.5% pure. International standard for silver jewelry.' },
};

export default function CertificatePage() {
  const { id } = useParams();
  const [cert, setCert] = useState(null);
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getCertificate(id).then(r => r.data),
      getCurrentRates().then(r => r.data).catch(() => null)
    ]).then(([certData, ratesData]) => {
      setCert(certData);
      setRates(ratesData);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!cert) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-lg mx-auto px-5 py-20 text-center">
          <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
          <h2 className="font-display text-2xl font-bold mb-3">Certificate Not Found</h2>
          <p className="text-muted-foreground mb-6">This certificate ID doesn't exist in our records.</p>
          <Link to="/orders"><button className="bg-foreground text-background px-6 py-2.5 rounded-full text-sm font-semibold">Back to Orders</button></Link>
        </div>
      </div>
    );
  }

  const metalType = cert.metal_type || 'gold';
  const isGold = metalType === 'gold';
  const purityLabel = cert.purity || (isGold ? '24K' : '999 Silver');
  const purityInfo = PURITY_INFO[purityLabel] || PURITY_INFO[isGold ? '24K' : '999 Silver'];
  const careGuide = CARE_GUIDES[metalType] || CARE_GUIDES.gold;
  const liveRate = cert.live_metal_rate || (rates && rates[metalType]?.rate_per_gram) || 0;
  const currentMetalValue = cert.current_metal_value || (liveRate * (cert.weight || 0) * ((cert.purity_fineness || 999) / 999));
  const mintDate = cert.mint_date ? new Date(cert.mint_date) : new Date();

  return (
    <div className="min-h-screen bg-background" data-testid="certificate-page">
      <Navbar />
      <div className="grain-overlay" />

      <div className="max-w-3xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <Link to="/orders" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8" data-testid="back-to-orders">
          <ArrowLeft className="w-4 h-4" /> Back to Orders
        </Link>

        {/* ===== CERTIFICATE CARD ===== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-card border border-white/5 rounded-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-gold/10 via-gold/5 to-transparent border-b border-white/5 px-6 md:px-8 py-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-gold" />
                  <span className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold">Digital Authenticity Certificate</span>
                </div>
                <p className="font-display text-2xl md:text-3xl font-bold" data-testid="cert-product-name">{cert.collectible_name}</p>
              </div>
              <div className="flex items-center gap-1.5 bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-semibold px-3 py-1.5 rounded-full">
                <CheckCircle className="w-3.5 h-3.5" /> Verified
              </div>
            </div>
          </div>

          <div className="px-6 md:px-8 py-8 space-y-8">

            {/* Product Image + QR side by side */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {cert.image_url && (
                <div className="md:col-span-2 aspect-[16/10] rounded-xl overflow-hidden bg-secondary">
                  <img src={cert.image_url} alt={cert.collectible_name} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex flex-col items-center justify-center bg-white rounded-xl p-5" data-testid="qr-section">
                {cert.qr_code_url && cert.qr_code_url.startsWith('/api/') ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${cert.qr_code_url}`}
                    alt="QR Code"
                    className="w-36 h-36 mb-3"
                    data-testid="qr-code-image"
                  />
                ) : (
                  <QrCode className="w-20 h-20 text-black/40 mb-3" />
                )}
                <p className="text-[10px] text-black/50 uppercase tracking-widest text-center">Scan to Verify</p>
                <p className="font-mono text-[10px] text-black/30 mt-1">{cert.serial_number}</p>
              </div>
            </div>

            <Separator className="bg-white/5" />

            {/* ===== PRODUCT DETAILS ===== */}
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-4 font-semibold">Product Details</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <DetailCard icon={<Gem className="w-4 h-4" />} label="Metal Type" value={isGold ? 'Gold' : 'Silver'} />
                <DetailCard icon={<span className="text-xs font-bold">g</span>} label="Weight" value={`${cert.weight}g`} />
                <DetailCard icon={<Shield className="w-4 h-4" />} label="Purity" value={purityLabel} />
                <DetailCard icon={<Sparkles className="w-4 h-4" />} label="Fineness" value={cert.purity_fineness || 999} />
                <DetailCard icon={<span className="text-xs font-bold">#</span>} label="Serial Number" value={cert.serial_number} mono />
                <DetailCard icon={<span className="text-xs">Cal</span>} label="Manufactured" value={mintDate.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })} />
              </div>
            </div>

            <Separator className="bg-white/5" />

            {/* ===== HALLMARK & CERTIFICATIONS ===== */}
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-4 font-semibold">Hallmark & Certifications</h3>
              <div className="bg-secondary/50 border border-white/5 rounded-xl p-5 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">BIS Hallmark</span>
                  <span className="font-semibold flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-green-400" /> {cert.bis_hallmark || 'BIS Certified'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Indian Standard</span>
                  <span className="font-mono text-xs">{purityInfo.standard}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Certificate Hash</span>
                  <span className="font-mono text-xs text-muted-foreground truncate max-w-[180px]">{cert.certificate_hash}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Certificate ID</span>
                  <span className="font-mono text-xs">{cert.certificate_id}</span>
                </div>
                {cert.mint_partner && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Mint Partner</span>
                    <span className="font-semibold">{cert.mint_partner}</span>
                  </div>
                )}
                <Separator className="bg-white/5 !my-2" />
                <p className="text-xs text-muted-foreground leading-relaxed">{purityInfo.description}</p>
              </div>
            </div>

            <Separator className="bg-white/5" />

            {/* ===== LIVE METAL VALUE ===== */}
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-4 font-semibold">Current Metal Value</h3>
              <div className="bg-gradient-to-r from-gold/5 to-transparent border border-gold/10 rounded-xl p-5">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{isGold ? 'Gold' : 'Silver'} price today</p>
                    <p className="font-display text-2xl font-bold text-gold flex items-center gap-2">
                      <span className={MONEY_CLASSNAME}>{formatInr(liveRate)}</span><span className="text-xs text-muted-foreground font-normal">/ gram</span>
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      <TrendingUp className="w-3 h-3 text-green-400" />
                      <span className="text-[11px] text-green-400 font-mono">Live Rate</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Your {cert.weight}g metal value</p>
                    <p className="font-display text-2xl font-bold text-foreground">
                      <span className={MONEY_CLASSNAME}>{formatInr(currentMetalValue)}</span>
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1 font-mono">
                      {cert.weight}g × {formatInr(liveRate)} × {((cert.purity_fineness || 999) / 999).toFixed(3)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Separator className="bg-white/5" />

            {/* ===== STORY BEHIND THE PIECE ===== */}
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-4 font-semibold">The Story</h3>
              <div className="space-y-3">
                <p className="text-sm text-foreground/70 leading-relaxed">
                  {cert.description || `This ${isGold ? 'gold' : 'silver'} piece is part of the ${cert.drop_name || 'SŌHM'} collection — handcrafted with precision, hallmarked for authenticity, and delivered with a unique serial identity.`}
                </p>
                <p className="text-sm text-foreground/70 leading-relaxed">
                  Every piece from SŌHM carries a BIS hallmark — the Government of India's assurance of purity. The serial number <span className="font-mono text-gold">{cert.serial_number}</span> is permanently engraved on the product, linking it to this digital certificate for lifetime verification.
                </p>
              </div>
            </div>

            <Separator className="bg-white/5" />

            {/* ===== CARE GUIDE ===== */}
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-4 font-semibold flex items-center gap-2">
                <Droplets className="w-4 h-4" /> {careGuide.title}
              </h3>
              <div className="space-y-4">
                {careGuide.tips.map((tip, i) => (
                  <div key={i} className="bg-secondary/30 border border-white/5 rounded-xl p-4">
                    <p className="text-sm font-semibold mb-1.5">{tip.heading}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{tip.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ===== ENGRAVING STATUS ===== */}
            <div className="bg-secondary/30 border border-white/5 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">Engraving Status</p>
                <p className="text-sm font-semibold">
                  {cert.engraving_status === 'engraved'
                    ? 'Serial number engraved on product'
                    : 'Pending engraving — serial will be engraved before shipping'}
                </p>
              </div>
              <div className={`w-3 h-3 rounded-full ${cert.engraving_status === 'engraved' ? 'bg-green-400' : 'bg-yellow-400 live-pulse'}`} />
            </div>
          </div>

          {/* Footer */}
          <div className="bg-secondary/20 border-t border-white/5 px-6 md:px-8 py-4 text-center">
            <p className="text-[11px] text-muted-foreground">
              This digital certificate is issued by SŌHM and can be verified anytime at{' '}
              <Link to="/verify" className="text-gold hover:underline">sohm.com/verify</Link>
            </p>
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
}

function DetailCard({ icon, label, value, mono }) {
  return (
    <div className="bg-secondary/30 border border-white/5 rounded-xl p-3.5">
      <div className="flex items-center gap-1.5 mb-1.5 text-muted-foreground">{icon}<span className="text-[10px] uppercase tracking-widest">{label}</span></div>
      <p className={`font-semibold text-sm ${mono ? 'font-mono text-xs' : ''}`}>{value}</p>
    </div>
  );
}
